import { useTranslation, useDirection, useProperty } from '@eti/providers';
import { breakpoints, Card, Inline, Notice, Stack, Text, textVariants, vars } from '@etg/wings';
import { css, cx } from '@eti/styles';
// oxlint-disable-next-line no-restricted-imports -- TODO WEBTECH-1501: Replace old schema object types with operation-specific types.

import { useRef } from 'react';
import { useSecondBaggage } from '../../second-baggage/utils/hooks/useSecondBag';
import { useCheckedBag } from '../utils/useCheckedBag';
import SecondBagApplySelectionToAll from '../../second-baggage/containers/SecondBagApplySelectionToAllContainer';
import { CHECK_IN_BAGGAGE, SECOND_BAGGAGE } from '../../../products';
import PersuasionModalProvider from '../../../common/context/PersuasionModalContext';
import { generateReadMore } from '../../../common/utils/readMore';
import getTranslatedTexts from '../../../common/text-resolvers/utils/getTranslatedTexts';
import SecondBagCheckBox from '../../second-baggage/containers/SecondBagCheckBoxContainer';
import { getIncludedBaggagePiecesWeightPrimaryFlow, getBaggageRadioButtonOptions } from '../utils/checkInBaggageUtils';
import BaggageSectionHeader from '../../common/components/BaggageSectionHeader';
import BaggageIllustration from '../../common/components/BaggageIllustration';
import BaggageStatus from '../../common/components/BaggageStatus';
import BaggageSectionDescription from '../../common/components/BaggageSectionDescription';
import BaggageSelection from '../../common/components/BaggageSelection';
import ApplySelectionToAllCheckbox from '../../common/components/ApplySelectionToAllCheckbox';
import useBaggageVisibilityTracker from '../../common/hooks/useBaggageVisibilityTracker';
import useSortedBaggageOptions from '../../common/hooks/useSortedBaggageOptions';
import { useLinksAccessibility } from '../../../custom-hooks/useLinkAccessibility';
import CheckInBaggageSelfTransferCard from './CheckInBaggageSelfTransferCard';
import { getIsShorterTransferTime } from '../../../common/utils/transferTimeUtils';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const generalTexts = {
  persuasionContent: 'Product.CheckInBaggage.CD.Persuasion.Content',
  additionalInformation: 'checkInBaggageHeaderAdditionalInformation',
  readMoreContent: 'checkInBaggageReadMoreModalContent',
  abstract: 'checkInBaggageHeaderDescription',
  title: 'checkInBaggageHeaderTitle',
  titleChoiceNo: 'noCheckInBaggageLabel',
  notIncludedContent: 'Product.CheckInBaggage.Not.Included.Or.Sellable.Text',
  longTransferTimeContent: 'Product.CheckInBaggage.LongTransferTime.Text',
  shortTransferTimeContent: 'Product.CheckInBaggage.ShortTransferTime.Text',
  includedBaggageSelfTransferWarning: 'Product.CheckInBaggage.IncludedBaggage.SelfTransferWarning.Text',
  notIncludedBaggageStatusLabel: 'TravelerDetails.Status.Unavailable',
  includedBaggageStatusLabel: 'TravelerDetails.Status.Included',
  sameChoiceForAllLabel: 'Product.CheckInBaggage.SelectSameChoiceForAll'
};
const baggageCardStyle = css`
  border: 1px solid ${vars.colors.divider};
  margin-block-end: 16px;
`;
const productSection = css`
  flex-wrap: nowrap;
`;
const includedStatus = css`
  justify-content: center;
`;
const notAvailableBaggageStatusTextStyle = css`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;
const baggageSelectionStyles = css`
  gap: 8px;
  margin-top: 2px;
  width: 100%;

  @media (min-width: ${breakpoints._768}) {
    gap: 16px;
    width: 85%;
  }
`;
const infantTextStyles = css`
  padding-bottom: 24px;
`;
const CheckInBaggagePrimaryFlow = ({
  bundleData,
  firstTravelerSelectionValue,
  form,
  includedBaggage,
  isBaggageAvailableOrIncluded,
  isCheckedBaggageSelected = false,
  isVI,
  order,
  orderSecondBag,
  onChange,
  onChangeAll,
  productName,
  productPosition,
  readMore = {
    isEnabled: true
  },
  shouldApplySelectionToAll,
  travelerNumber,
  totalBaggageQuantity
}) => {
  var _generateReadMore;
  const {
    p
  } = useProperty();
  const {
    t
  } = useTranslation();
  const {
    isRTL
  } = useDirection();
  const rootElement = useRef(null);
  const isSelfTransferDealEnabled = p('IbeClient.VI.SelfTransferDeal.Enabled');
  const minimumConnectionTime = p('IbeClient.ShowNoSelfTransferGuaranteeInfo.MinLayoverMinutes');
  const {
    notIncludedContent,
    longTransferTimeContent,
    shortTransferTimeContent,
    includedBaggageSelfTransferWarning,
    titleChoiceNo,
    readMoreContent,
    title,
    notIncludedBaggageStatusLabel,
    includedBaggageStatusLabel,
    abstract,
    sameChoiceForAllLabel,
    persuasionContent
  } = getTranslatedTexts(generalTexts, t);
  useLinksAccessibility({
    targetElement: rootElement,
    productTitle: title
  });
  // Checked-Bag Data
  const {
    checkedBagOptions,
    hasMultipleWeightOptions,
    isInfant,
    isFirstTraveler,
    isCheckedBaggageIncludedInTrip,
    isCheckedBagPreselected,
    traveler,
    hasMultipleNonInfantTravelers
  } = useCheckedBag({
    travelerNumber,
    order,
    bundleData,
    includedBaggage
  });
  useBaggageVisibilityTracker({
    isBaggageAvailableOrIncluded,
    includedBaggageInTrip: isCheckedBaggageIncludedInTrip,
    productName,
    rootElement,
    productPosition
  });
  const availableForPurchase = isBaggageAvailableOrIncluded && !isCheckedBaggageIncludedInTrip;
  const notAvailableForPurchase = !isBaggageAvailableOrIncluded && !isCheckedBaggageIncludedInTrip;

  // Second-Bag Data
  const {
    hasSecondBag,
    secondBagTraveler,
    secondBagTravelers,
    secondBagOptions
  } = useSecondBaggage({
    travelerNumber,
    orderSecondBag
  });
  const {
    baggageOptions
  } = getBaggageRadioButtonOptions({
    checkedBagOptions,
    hasMultipleWeightOptions,
    secondBagOptions,
    traveler,
    titleChoiceNo,
    secondBagTraveler,
    secondBagProductId: SECOND_BAGGAGE.id,
    t
  });
  const sortedBaggageOptions = useSortedBaggageOptions(order.bounds, baggageOptions);
  const showAbstractText = abstract && !isCheckedBagPreselected;
  const canShowTransferTimeMessage = isVI && isSelfTransferDealEnabled;
  const isShorterTransferTime = getIsShorterTransferTime(order.bounds, minimumConnectionTime);
  const transferTimeMessage = isShorterTransferTime ? shortTransferTimeContent : longTransferTimeContent;
  const baggageStatusDescription = canShowTransferTimeMessage ? transferTimeMessage : notIncludedContent;
  const canShowIncludedBaggageSelfTransferWarning = isCheckedBaggageIncludedInTrip && isVI && isSelfTransferDealEnabled && isShorterTransferTime;
  const secondBagAbstract = canShowIncludedBaggageSelfTransferWarning ? undefined : abstract;
  return isInfant ? /*#__PURE__*/_jsx(Text, {
    as: "p",
    className: infantTextStyles,
    "data-testid": "notAvailableBaggage_infants",
    variant: textVariants._14,
    children: t('Product.Infant.Note')
  }) : /*#__PURE__*/_jsx("div", {
    ref: rootElement,
    children: /*#__PURE__*/_jsx(Card, {
      className: cx('travelerDetailsSection', baggageCardStyle),
      children: /*#__PURE__*/_jsxs(Stack, {
        "data-testid": `travelerDetails-productSection-product-${productName}-body-${traveler === null || traveler === void 0 ? void 0 : traveler.id}`,
        children: [/*#__PURE__*/_jsxs(Stack, {
          spacing: 8,
          children: [/*#__PURE__*/_jsx(BaggageSectionHeader, {
            productName: productName,
            readMoreModal: (_generateReadMore = generateReadMore(readMore, readMoreContent)) !== null && _generateReadMore !== void 0 ? _generateReadMore : undefined,
            title: title,
            travelerId: (traveler === null || traveler === void 0 ? void 0 : traveler.id) || ''
          }), /*#__PURE__*/_jsx(BaggageSectionDescription, {
            description: t('Product.CheckedBaggagePerTraveler.Description.Label'),
            productName: productName,
            travelerId: (traveler === null || traveler === void 0 ? void 0 : traveler.id) || ''
          })]
        }), /*#__PURE__*/_jsxs(Inline, {
          className: productSection,
          children: [/*#__PURE__*/_jsx(BaggageIllustration, {
            isRTL: isRTL,
            productName: productName
          }), notAvailableForPurchase && /*#__PURE__*/_jsx(BaggageStatus, {
            className: notAvailableBaggageStatusTextStyle,
            dataTestId: "notAvailableBaggage-status",
            description: baggageStatusDescription,
            statusLabel: notIncludedBaggageStatusLabel,
            useNotice: true,
            variant: "caution"
          }), isCheckedBaggageIncludedInTrip && (hasSecondBag ? /*#__PURE__*/_jsxs(Stack, {
            className: includedStatus,
            children: [/*#__PURE__*/_jsx(BaggageStatus, {
              dataOfferRate: CHECK_IN_BAGGAGE.id,
              dataTestId: "includedBaggage-status",
              description: getIncludedBaggagePiecesWeightPrimaryFlow(includedBaggage, bundleData, t),
              statusLabel: includedBaggageStatusLabel,
              variant: "positive"
            }), /*#__PURE__*/_jsx(SecondBagCheckBox, {
              abstract: secondBagAbstract,
              firstTravelerSelectionValue: firstTravelerSelectionValue,
              form: form,
              isFirstTraveler: isFirstTraveler,
              orderSecondBag: orderSecondBag,
              productName: productName,
              secondBagTraveler: secondBagTraveler,
              secondBagTravelers: secondBagTravelers,
              travelerNumber: travelerNumber
            })]
          }) : /*#__PURE__*/_jsx(BaggageStatus, {
            dataOfferRate: CHECK_IN_BAGGAGE.id,
            dataTestId: "includedBaggage-status",
            description: getIncludedBaggagePiecesWeightPrimaryFlow(includedBaggage, bundleData, t),
            statusLabel: includedBaggageStatusLabel,
            variant: "positive"
          })), availableForPurchase ? /*#__PURE__*/_jsxs(Stack, {
            className: baggageSelectionStyles,
            children: [/*#__PURE__*/_jsx(PersuasionModalProvider, {
              children: /*#__PURE__*/_jsx(BaggageSelection, {
                baggageOptions: sortedBaggageOptions,
                firstTravelerSelectionValue: firstTravelerSelectionValue,
                onChange: onChange,
                order: order,
                persuasionContent: persuasionContent,
                persuasionTitle: title,
                productName: productName,
                productPosition: productPosition,
                radioGroupScreenReaderDescription: t('Product.CheckedBaggagePerTraveler.Description.Label'),
                rootElement: rootElement,
                secondBagProductId: hasSecondBag ? SECOND_BAGGAGE.id : undefined,
                shouldApplySelectionToAll: shouldApplySelectionToAll,
                totalBaggageQuantity: totalBaggageQuantity,
                travelerId: traveler.id
              })
            }), showAbstractText && /*#__PURE__*/_jsx(Notice, {
              variant: "promotional",
              children: /*#__PURE__*/_jsx(Text, {
                asHtml: true,
                variant: textVariants._14_BOLD,
                children: abstract
              })
            })]
          }) : null]
        }), isCheckedBaggageSelected && isVI && isSelfTransferDealEnabled && /*#__PURE__*/_jsx(CheckInBaggageSelfTransferCard, {}), canShowIncludedBaggageSelfTransferWarning && /*#__PURE__*/_jsx(Notice, {
          "data-testid": "includedBaggage-selfTransferWarning",
          variant: "caution",
          children: includedBaggageSelfTransferWarning
        }), availableForPurchase && isFirstTraveler && hasMultipleNonInfantTravelers && /*#__PURE__*/_jsx(ApplySelectionToAllCheckbox, {
          firstTravelerSelectionValue: firstTravelerSelectionValue,
          label: sameChoiceForAllLabel,
          onChangeAll: onChangeAll,
          productName: productName,
          shouldApplySelectionToAll: shouldApplySelectionToAll
        }), isCheckedBaggageIncludedInTrip && isFirstTraveler && hasSecondBag && secondBagTravelers.length > 1 && /*#__PURE__*/_jsx(SecondBagApplySelectionToAll, {
          firstTravelerSelectionValue: firstTravelerSelectionValue,
          form: form,
          label: sameChoiceForAllLabel,
          orderSecondBag: orderSecondBag,
          productName: productName,
          secondBagTravelers: secondBagTravelers
        })]
      })
    })
  });
};
export default CheckInBaggagePrimaryFlow;
//# sourceMappingURL=CheckInBaggagePrimaryFlow.js.map